import React from 'react';
import { Role, getRoleById, getAllRoles, getCommonGoalText } from '../components/data/roles';
import { useNavigate, useParams } from 'react-router-dom';

const RoleTemplate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    // Get roles data - either specific role or all roles
    const roles: Role[] = id
        ? [getRoleById(id)].filter(Boolean) as Role[]
        : getAllRoles();

    const commonGoal = getCommonGoalText();

    // Color mappings for different roles
    const roleColors = {
        'senior-therapist': 'bg-purple text-softwhite',
        'senior-track-therapist': 'bg-teal-500 text-softwhite',
        'associate-therapist': 'bg-earthybeige text-charcol'
    };

    return (
        <div className="min-h-screen bg-softwhite p-4 md:p-8">
            {/* Header */}
            <div className="text-center mb-12 mt-20">
                <h1 className="text-3xl md:text-4xl font-bold text-charcol mb-4">
                    गृह प्रवेश® Therapy and Healing Centre
                </h1>
                <p className="text-xl md:text-2xl italic text-sage mb-6">
                    {commonGoal}
                </p>
                <div className="h-1 w-32 bg-golden-yellow mx-auto"></div>
            </div>

            {/* Roles Section */}
            <div className="max-w-6xl mx-auto grid gap-8 md:gap-12">
                {roles.map((role) => (
                    <div
                        key={role.id}
                        className="bg-white rounded-lg shadow-lg overflow-hidden border-l-4 border-palegola"
                    >
                        {/* Role Header */}
                        <div className={`p-4 ${roleColors[role.id as keyof typeof roleColors] || 'bg-sage text-softwhite'}`}>
                            <h2 className="text-2xl font-bold">{role.title}</h2>
                        </div>

                        {/* Role Content */}
                        <div className="p-6">
                            {/* Description */}
                            <div className="mb-6">
                                <p className="text-charcol">{role.description}</p>
                            </div>

                            {/* Important Notes */}
                            <div className="mb-6 bg-orange-50 p-4 rounded-md border-l-4 border-orange-500">
                                <h3 className="text-lg font-semibold text-orange-700 mb-2">Important Notes</h3>
                                <ul className="list-disc pl-5 text-charcol">
                                    {role.importantNote.map((note, index) => (
                                        <li key={index} className="mb-2">{note}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* Role Description */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-charcol mb-2">Role Overview</h3>
                                <p className="text-charcol">{role.roleDescription}</p>
                                {role.isSupervisoryRole && (
                                    <span className="inline-block mt-2 bg-purple text-softwhite text-sm px-3 py-1 rounded-full">
                                        Supervisory Role
                                    </span>
                                )}
                            </div>

                            {/* Responsibilities */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-charcol mb-2">Responsibilities</h3>
                                <ul className="list-disc pl-5 text-charcol">
                                    {role.responsibilities.map((resp) => (
                                        <li key={resp.id} className="mb-2">{resp.description}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* Compensation */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-charcol mb-2">Compensation</h3>
                                <ul className="list-disc pl-5 text-charcol">
                                    {role.compensation.map((comp) => (
                                        <li key={comp.id} className="mb-2">{comp.description}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* Fees (if applicable) */}
                            {role.fees && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-charcol mb-2">Fees</h3>
                                    <div className="space-y-4">
                                        {role.fees.map((fee) => (
                                            <div key={fee.id} className="bg-sage bg-opacity-10 p-4 rounded-md">
                                                <h4 className="font-medium text-charcol">{fee.description}</h4>
                                                {fee.amount && (
                                                    <p className="mt-1 text-sm bg-golden-yellow bg-opacity-20 inline-block px-2 py-1 rounded">
                                                        {fee.amount}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Time Commitments */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-charcol mb-2">Time Commitments</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {role.timeCommitments.map((time, index) => (
                                        <div key={index} className="flex items-center border border-sage rounded-md p-3">
                                            <div className="w-10 h-10 flex-shrink-0 rounded-full bg-teal-500 text-white flex items-center justify-center mr-3">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-charcol font-medium">{time.description}</p>
                                                <p className="text-sage text-sm">{time.duration}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Tenure (if applicable) */}
                            {role.tenure && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-charcol mb-2">Tenure</h3>
                                    <p className="text-charcol">{role.tenure}</p>
                                </div>
                            )}

                            {/* Qualifications */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-charcol mb-2">Qualifications</h3>
                                <ul className="list-disc pl-5 text-charcol">
                                    {role.qualifications.map((qual) => (
                                        <li key={qual.id} className="mb-2">{qual.description}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className="text-center mt-8 mb-4">
                                <a
                                    href={role.docLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-4 py-2 bg-sage hover:bg-sage/80 text-softwhite rounded-md transition-colors duration-300"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                    </svg>
                                    <span>View Full Document</span>
                                </a>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Join Us Button */}
            <div className="text-center mt-12 mb-6">
                <button
                    onClick={() => navigate('/join-us')}
                    className="px-8 py-3 bg-golden-yellow hover:bg-palegola text-charcol font-bold rounded-md transition-colors duration-300 shadow-md flex items-center mx-auto"
                >
                    <span>Join Our Team</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default RoleTemplate;